import type { Action } from "app/services/types.generated";
import { isFrontCameraName } from "lib/mission/frontPhotoWaypointGuard";

export type PayloadHealthIssueId = "gimbal" | "insta360" | "arm";

export type PayloadHealthSnapshot = {
    hasGimbalVideo: boolean;
    hasGimbalControl: boolean;
    insta360Fsm: string;
    hasArm: boolean;
};

const INSTA360_CONNECTED_FSMS = new Set(["idle", "recording", "downloading"]);

export function isInsta360SessionConnected(fsm: string): boolean {
    return INSTA360_CONNECTED_FSMS.has(String(fsm || "").trim().toLowerCase());
}

function takePhotoUsesGimbal(args: unknown): boolean {
    if (!args || typeof args !== "object") return false;
    const camera = (args as { camera_name?: unknown }).camera_name;
    return typeof camera === "string" && !isFrontCameraName(camera);
}

export function missionActionsSorted(actions: Action[]): Action[] {
    return [...actions].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
}

/** Index for `start_step_index`, or null if the selected action no longer exists. */
export function missionStartStepIndexForActionId(actions: Action[], actionId: string): number | null {
    const index = missionActionsSorted(actions).findIndex((action) => action.id === actionId);
    return index < 0 ? null : index;
}

/**
 * Payloads that upcoming mission steps need but that are not healthy right now.
 * `startStepIndex` matches mission executor indexing (sorted by sequence order).
 */
export function collectPayloadHealthIssues(
    actions: Action[],
    startStepIndex: number,
    health: PayloadHealthSnapshot,
): PayloadHealthIssueId[] {
    const start = Math.max(0, Math.floor(startStepIndex));
    const remaining = missionActionsSorted(actions).slice(start);
    const issues = new Set<PayloadHealthIssueId>();

    let needsGimbal = false;
    let needsInsta360 = false;
    let needsArm = false;
    for (const action of remaining) {
        if (action.actionType === "TakePhoto" && takePhotoUsesGimbal(action.args)) {
            needsGimbal = true;
        }
        if (action.actionType === "Record360") {
            needsInsta360 = true;
        }
        if (
            action.actionType === "PressElevatorButton" ||
            action.actionType === "ArmPreset"
        ) {
            needsArm = true;
        }
    }

    if (needsGimbal && (!health.hasGimbalVideo || !health.hasGimbalControl)) {
        issues.add("gimbal");
    }
    if (needsInsta360 && !isInsta360SessionConnected(health.insta360Fsm)) {
        issues.add("insta360");
    }
    if (needsArm && !health.hasArm) {
        issues.add("arm");
    }

    return [...issues];
}
